import { jobCategoryTree } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient, type QueryFilters } from '@tanstack/react-query'
import { Fragment, useMemo, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { ColourPicker, nextColour } from '../../components/ColourPicker.js'
import { Icon } from '../../components/Icon.js'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  SearchBox,
  SkeletonRows,
  Toolbar,
} from '../../components/ui.js'
import { ServicesCard } from './ServicesCard.js'
import { useBandDrag } from '../../lib/band-drag.js'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

/**
 * The list every job's "Kind of work" picks from.
 *
 * Two levels: a group, and the kinds of work inside it. A subcategory is a
 * category with a parent rather than a second model, so a row here can become a
 * group or stop being one by changing one field, and a job goes on pointing at
 * exactly one of them. Decision 71.
 *
 * **A group is not a thing you create, it is a row with something in it**, and
 * the first version of this screen expressed that as a `<select>` per row
 * reading "No group" thirty times. Every mechanism was there and nobody could
 * find it. So the shape of the list *is* the interface now: rows sit under the
 * row they belong to, a drag moves them, and every top-level row carries an
 * **Add inside** button — which is what makes a group, and says so.
 *
 * Three things are deliberate:
 *
 * - **Order is stored, so it has to be draggable.** `sortOrder` existed and
 *   nothing wrote it, which left the office an alphabetical list of 30 kinds of
 *   work when the four they book daily are what belong at the top. A drag sends
 *   the whole level through `reorderCategories`, so a position and a move are
 *   one write and cannot half-land.
 * - **Search turns dragging off.** The visible order is not the stored order
 *   while a term is in, so a drop would file a row against a neighbour that is
 *   only next to it because of the filter.
 * - **The `<select>` stays.** Drag is the shortcut for somebody with a mouse;
 *   the group picker on each row is the path that works with a keyboard, on a
 *   phone, and in a screen reader. Touch is left to the browser for the same
 *   reason as the people list — a finger dragging a row is a finger scrolling.
 *
 * **Removing has two meanings and both are offered.** Retire takes a kind of
 * work off every picker and leaves it on the jobs that already have it, which
 * is what "we don't do that any more" means for a label on 16,000 jobs. Delete
 * is for the one somebody typed by mistake, and the server refuses it the
 * moment anything points at it rather than quietly emptying a column.
 */

type Category = RouterOutputs['jobs']['categories'][number]

/** The band the top level draws itself as, where the groups themselves sit. */
const TOP = '__top__'

/** A group entry, or a top-level row with nothing inside it yet. */
interface Entry {
  category: Category
  children: Category[]
}

/**
 * Two catalogues, on one tab.
 *
 * The kinds of work a job can be, and the services it can carry. They are
 * different lists doing different jobs — a category labels and colours the
 * job, a service is the unit of work on it and now the unit of billing too
 * (rule 11) — and they sit together because "what we sell" is one question
 * the office comes here to answer.
 */
export function CatalogPanel() {
  return (
    <div className="flex flex-col gap-4">
      <JobCategoriesCard />
      <ServicesCard />
    </div>
  )
}

function JobCategoriesCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [showRetired, setShowRetired] = useState(false)
  const [q, setQ] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const categories = useQuery(
    trpc.jobs.categories.queryOptions({ includeArchived: showRetired }),
  )

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    // The board tints blocks by category colour, so a recolour has to reach it.
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }

  const upsert = useMutation(trpc.jobs.upsertCategory.mutationOptions({ onSuccess: invalidate }))
  const archive = useMutation(trpc.jobs.archiveCategory.mutationOptions({ onSuccess: invalidate }))
  const remove = useMutation(trpc.jobs.deleteCategory.mutationOptions({ onSuccess: invalidate }))

  /**
   * Reordering, with the list corrected before the server answers.
   *
   * Without it the row springs back to where it was picked up, sits there for a
   * round trip and then jumps, which reads as the drag having failed and then
   * being undone by somebody else. The snapshot goes back if the write is
   * refused, and either way the query is invalidated — the server is the one
   * that knows what was allowed.
   */
  const listFilter = trpc.jobs.categories.queryFilter() as QueryFilters

  const reorder = useMutation(
    trpc.jobs.reorderCategories.mutationOptions({
      onMutate: ({ parentId, orderedIds }) => {
        const snapshot = queryClient.getQueriesData<Category[]>(listFilter)
        const at = new Map(orderedIds.map((id, index) => [id, index]))

        for (const [key, data] of snapshot) {
          if (!data) continue
          queryClient.setQueryData(
            key,
            data.map((row) => {
              const index = at.get(row.id)
              return index === undefined
                ? row
                : { ...row, parentId: parentId ?? null, sortOrder: index }
            }),
          )
        }

        return snapshot
      },
      onError: (_error, _input, snapshot) => {
        for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data)
      },
      onSettled: invalidate,
    }),
  )

  const rows = categories.data ?? []
  const term = q.trim().toLowerCase()

  /**
   * The top level in order, each entry carrying what is inside it.
   *
   * `jobCategoryTree` already interleaves groups with the rows that are not in
   * one, which is the order the office set; this only flattens its two shapes
   * into the one this screen draws.
   */
  const entries = useMemo<Entry[]>(() => {
    return jobCategoryTree(rows).flatMap((section) =>
      section.group
        ? [{ category: section.group, children: section.options.slice(1) }]
        : section.options.map((category) => ({ category, children: [] })),
    )
  }, [rows])

  /**
   * The same list, narrowed by the search box.
   *
   * A group survives if it matches or if anything inside it does, because
   * hiding the heading of a match leaves the match looking top-level.
   */
  const shown = useMemo<Entry[]>(() => {
    if (!term) return entries

    const hit = (category: Category) => category.name.toLowerCase().includes(term)

    return entries.flatMap((entry) => {
      if (hit(entry.category)) return [entry]
      const children = entry.children.filter(hit)
      return children.length > 0 ? [{ ...entry, children }] : []
    })
  }, [entries, term])

  const groupIds = useMemo(
    () => new Set(entries.filter((entry) => entry.children.length > 0).map((e) => e.category.id)),
    [entries],
  )

  // Only a top-level row can be a group, because a third level is refused.
  const groupChoices = useMemo(
    () =>
      rows
        .filter((row) => !row.parentId && !row.archivedAt)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  )

  const toggle = (id: string) =>
    setCollapsed((was) => {
      const next = new Set(was)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const open = (id: string) =>
    setCollapsed((was) => {
      if (!was.has(id)) return was
      const next = new Set(was)
      next.delete(id)
      return next
    })

  /** Dragging is off while a term is in: the order on screen is not the order. */
  const draggable = !term

  const drag = useBandDrag({
    enabled: draggable,
    onDrop: (id, band, before) => {
      setNotice(null)

      const parentId = band === TOP ? null : band

      // The pointer never left the row it picked up, so nothing moved.
      if (before === id) return

      if (parentId && groupIds.has(id)) {
        const into = rows.find((row) => row.id === parentId)?.name ?? 'another group'
        const count = entries.find((entry) => entry.category.id === id)?.children.length ?? 0
        setNotice(
          `That is a group with ${count} ${count === 1 ? 'kind' : 'kinds'} of work inside it, so it stays at the top level. Move those into ${into} instead.`,
        )
        return
      }

      const members =
        band === TOP
          ? entries.map((entry) => entry.category.id)
          : (entries.find((entry) => entry.category.id === band)?.children ?? []).map((c) => c.id)

      const without = members.filter((member) => member !== id)
      // An insertion point that is not in this band — the strip under a folded
      // group is the one that does this — means the end of it.
      const at = before && without.includes(before) ? without.indexOf(before) : without.length
      const orderedIds = [...without.slice(0, at), id, ...without.slice(at)]

      const wasHere = rows.find((row) => row.id === id)?.parentId === parentId
      if (wasHere && orderedIds.every((value, index) => value === members[index])) return

      if (parentId) open(parentId)
      reorder.mutate({ parentId, orderedIds })
    },
  })

  const carried = drag.carrying
    ? rows.find((row) => row.id === drag.carrying?.id)
    : undefined

  /** Where a release would put the caret, or null while nothing is in flight. */
  const landing = drag.carrying
    ? groupIds.has(drag.carrying.id) && drag.over !== TOP
      ? null
      : drag.over
    : null

  const caret = (band: string, beforeId: string | null) =>
    landing === band && drag.before === beforeId && drag.carrying?.id !== beforeId ? (
      <li aria-hidden className="relative h-0">
        <span className="absolute inset-x-2 -top-px h-0.5 rounded-full bg-brand-600" />
      </li>
    ) : null

  const error = upsert.error ?? archive.error ?? remove.error ?? reorder.error
  const message = notice ?? (error ? errorMessage(error) : null)

  const allOpen = collapsed.size === 0

  return (
    <Card>
      <CardHeader
        title="Kinds of work"
        action={
          <Button
            size="sm"
            variant="secondary"
            icon={<Icon name="plus" className="size-3.5" />}
            onClick={() => {
              setQ('')
              setAddingTo(TOP)
            }}
          >
            New
          </Button>
        }
      />

      <p className="px-[var(--card-pad)] pt-2 text-xs text-ink-500">
        What the New job form offers, and what colours a booking when the board is coloured by
        category. Drag a row to reorder it or to put it inside a group — two levels, no deeper.
      </p>

      <Toolbar className="mt-2">
        <div className="min-w-0 w-full sm:w-[240px] sm:flex-none">
          <SearchBox value={q} onChange={setQ} placeholder="Search kinds of work" className="sm:w-full" />
        </div>

        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            setCollapsed(allOpen ? new Set(groupIds) : new Set())
          }
        >
          {allOpen ? 'Collapse groups' : 'Expand groups'}
        </Button>

        <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-600">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(event) => setShowRetired(event.target.checked)}
          />
          Show retired
        </label>
      </Toolbar>

      {message ? (
        <p
          role="alert"
          className="border-b border-red-200 bg-red-50 px-[var(--row-pad-x)] py-2 text-xs text-red-700"
        >
          {message}
        </p>
      ) : null}

      {categories.isPending ? (
        <SkeletonRows rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No kinds of work yet"
          description="Add the first one below, or run the ServiceM8 import to bring the existing list across."
        />
      ) : shown.length === 0 ? (
        <EmptyState title="Nothing matches that" description="Try part of a name." />
      ) : (
        <ul data-band={draggable ? TOP : undefined} className="divide-y divide-ink-100">
          {shown.map((entry) => {
            const isGroup = entry.children.length > 0
            const shut = collapsed.has(entry.category.id) && !term
            const jobs =
              entry.category._count.jobs +
              entry.children.reduce((sum, child) => sum + child._count.jobs, 0)

            return (
              <Fragment key={entry.category.id}>
                {caret(TOP, entry.category.id)}

                <li
                  data-row={entry.category.id}
                  className={cn(
                    'bg-white',
                    landing === entry.category.id && 'bg-brand-50',
                  )}
                >
                  <CategoryRow
                    category={entry.category}
                    jobs={jobs}
                    inside={isGroup ? entry.children.length : null}
                    collapsed={shut}
                    lifted={drag.carrying?.id === entry.category.id}
                    draggable={draggable}
                    groupChoices={
                      isGroup
                        ? []
                        : groupChoices.filter((choice) => choice.id !== entry.category.id)
                    }
                    onGrab={(event) => drag.begin(event, entry.category.id, TOP)}
                    onToggle={isGroup ? () => toggle(entry.category.id) : undefined}
                    onAddInside={() => {
                      open(entry.category.id)
                      setAddingTo(entry.category.id)
                    }}
                    onChange={(patch) => upsert.mutate({ id: entry.category.id, ...patch })}
                    onArchive={() =>
                      archive.mutate({
                        id: entry.category.id,
                        archived: entry.category.archivedAt === null,
                      })
                    }
                    onDelete={() => remove.mutate({ id: entry.category.id })}
                  />

                  {/* A folded group is still somewhere to put things, and the
                      rows to aim between are not on screen. The strip only
                      exists while something is in flight. */}
                  {shut && isGroup && drag.carrying && !groupIds.has(drag.carrying.id) ? (
                    <div
                      data-band={entry.category.id}
                      className={cn(
                        'mx-[var(--row-pad-x)] mb-2 rounded-lg border border-dashed px-2 py-1.5 text-center text-[11px]',
                        landing === entry.category.id
                          ? 'border-brand-500 text-brand-700'
                          : 'border-ink-300 text-ink-400',
                      )}
                    >
                      Drop into {entry.category.name}
                    </div>
                  ) : null}

                  {shut || (!isGroup && addingTo !== entry.category.id) ? null : (
                    <ul
                      data-band={draggable ? entry.category.id : undefined}
                      className="divide-y divide-ink-100 border-t border-ink-100 bg-ink-50/40"
                    >
                      {entry.children.map((child) => (
                        <Fragment key={child.id}>
                          {caret(entry.category.id, child.id)}
                          <li data-row={child.id} className="pl-6">
                            <CategoryRow
                              category={child}
                              jobs={child._count.jobs}
                              inside={null}
                              lifted={drag.carrying?.id === child.id}
                              draggable={draggable}
                              groupChoices={groupChoices.filter(
                                (choice) => choice.id !== child.id,
                              )}
                              onGrab={(event) =>
                                drag.begin(event, child.id, entry.category.id)
                              }
                              onChange={(patch) => upsert.mutate({ id: child.id, ...patch })}
                              onArchive={() =>
                                archive.mutate({
                                  id: child.id,
                                  archived: child.archivedAt === null,
                                })
                              }
                              onDelete={() => remove.mutate({ id: child.id })}
                            />
                          </li>
                        </Fragment>
                      ))}

                      {caret(entry.category.id, null)}

                      {addingTo === entry.category.id ? (
                        <DraftRow
                          indented
                          placeholder="Kind of work"
                          used={rows.map((row) => row.colour)}
                          pending={upsert.isPending}
                          onCancel={() => setAddingTo(null)}
                          onSave={(name, colour) =>
                            upsert.mutate({ name, colour, parentId: entry.category.id })
                          }
                        />
                      ) : null}
                    </ul>
                  )}
                </li>
              </Fragment>
            )
          })}

          {caret(TOP, null)}

          {addingTo === TOP ? (
            <DraftRow
              placeholder="Kind of work, or a group name"
              used={rows.map((row) => row.colour)}
              pending={upsert.isPending}
              onCancel={() => setAddingTo(null)}
              onSave={(name, colour) => upsert.mutate({ name, colour, parentId: null })}
            />
          ) : null}
        </ul>
      )}

      <NewCategoryForm
        groupChoices={groupChoices}
        used={rows.map((row) => row.colour)}
        pending={upsert.isPending}
        onSave={(input) => upsert.mutate(input)}
      />

      <div className="flex items-center gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-2">
        <span className="text-xs text-ink-400">
          {term ? `${shown.length} of ${entries.length} shown · ` : null}
          {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
          {groupIds.size > 0 ? ` in ${groupIds.size} ${groupIds.size === 1 ? 'group' : 'groups'}` : null}
        </span>
        {term ? (
          <span className="ml-auto text-xs text-ink-400">Clear the search to reorder.</span>
        ) : null}
      </div>

      {carried ? (
        <CarriedRow
          ref={drag.ghostRef}
          category={carried}
          // Only when it is changing hands. Within one band the caret already
          // says where, and "→ Top level" on every nudge is noise.
          landing={
            landing === null || landing === drag.carrying?.from
              ? null
              : landing === TOP
                ? 'Top level'
                : (rows.find((row) => row.id === landing)?.name ?? null)
          }
        />
      ) : null}
    </Card>
  )
}

/* -------------------------------------------------------------------------- */

function CategoryRow({
  category,
  jobs,
  inside,
  collapsed,
  lifted,
  draggable,
  groupChoices,
  onGrab,
  onToggle,
  onAddInside,
  onChange,
  onArchive,
  onDelete,
}: {
  category: Category
  /** Jobs on this row, or across the group when it has children. */
  jobs: number
  /** How many kinds of work are in here, or null when this is not a group. */
  inside: number | null
  collapsed?: boolean
  lifted: boolean
  draggable: boolean
  /** Empty on a top-level row: it is already where the groups live. */
  groupChoices: Category[]
  onGrab: (event: ReactPointerEvent) => void
  onToggle?: () => void
  onAddInside?: () => void
  onChange: (patch: { name?: string; colour?: string; parentId?: string | null }) => void
  onArchive: () => void
  onDelete: () => void
}) {
  const retired = category.archivedAt !== null
  const held = category._count.jobs + category._count.children + category._count.templates

  return (
    <div
      onPointerDown={draggable ? onGrab : undefined}
      className={cn(
        'flex flex-wrap items-center gap-2 py-[var(--row-pad-y)] pr-[var(--row-pad-x)] pl-[var(--row-pad-x)]',
        draggable && 'cursor-grab',
        retired && 'bg-ink-50',
        lifted && 'opacity-40',
      )}
    >
      {/* The grip says the row can be picked up; the row itself is the handle,
          because asking somebody to hit a 16px column of dots is what made the
          people list look like it had no drag at all. */}
      <span
        aria-label={draggable ? `Move ${category.name}` : undefined}
        className={cn('w-4 shrink-0', draggable ? 'text-ink-300' : 'invisible')}
      >
        <Icon name="grip" className="size-4" />
      </span>

      {onToggle ? (
        <button
          type="button"
          data-no-drag
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Show' : 'Hide'} what is inside ${category.name}`}
          className="shrink-0 rounded-sm p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
        >
          <Icon
            name="chevronDown"
            className={cn('size-3.5 transition-transform', collapsed && '-rotate-90')}
            strokeWidth={2.25}
          />
        </button>
      ) : (
        <span className="w-[18px] shrink-0" aria-hidden />
      )}

      <span data-no-drag className="shrink-0">
        <ColourPicker
          value={category.colour}
          label={`Colour of ${category.name}`}
          onChange={(colour) => onChange({ colour })}
        />
      </span>

      <Input
        // Keyed on the name so a rename from elsewhere reaches an untouched box;
        // `defaultValue` alone would keep showing the stale one forever.
        key={category.name}
        defaultValue={category.name}
        aria-label={`Name of ${category.name}`}
        className={cn(
          'min-w-32 flex-1 border-0 bg-transparent px-1.5 shadow-none ring-0',
          'hover:bg-white hover:ring-1 hover:ring-ink-200 focus:bg-white focus:ring-2',
          inside !== null && 'font-medium',
        )}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            event.currentTarget.value = category.name
            event.currentTarget.blur()
          }
        }}
        onBlur={(event) => {
          const next = event.target.value.trim()
          if (next && next !== category.name) onChange({ name: next })
        }}
      />

      {inside !== null ? (
        <Badge>{inside === 1 ? '1 inside' : `${inside} inside`}</Badge>
      ) : null}

      {/* The path onto a group that does not need a mouse. Quiet until it is
          reached for, because a column of the same word thirty times down the
          page was most of what made this list unreadable. */}
      {groupChoices.length > 0 ? (
        <label data-no-drag className="shrink-0">
          <span className="sr-only">{`Group for ${category.name}`}</span>
          <select
            aria-label={`Group for ${category.name}`}
            value={category.parentId ?? ''}
            onChange={(event) => onChange({ parentId: event.target.value || null })}
            className="h-[var(--control-sm)] max-w-28 rounded-md border-0 bg-transparent px-1 text-xs text-ink-500 hover:bg-white hover:text-ink-900 hover:ring-1 hover:ring-ink-200 focus:ring-2 focus:ring-brand-600"
          >
            <option value="">No group</option>
            {groupChoices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {retired ? <Badge tone="amber">Retired</Badge> : null}

      <span
        className="w-20 shrink-0 text-right text-xs tabular-nums text-ink-500"
        title={inside === null ? undefined : 'Jobs across this group'}
      >
        {jobs.toLocaleString('en-AU')} {jobs === 1 ? 'job' : 'jobs'}
      </span>

      {/* Putting something in here is what turns a row into a group, so this
          is on every top-level row rather than only the ones that already are
          one. It is the answer to "where do I make a group". */}
      {onAddInside ? (
        <Button
          size="sm"
          variant="ghost"
          data-no-drag
          aria-label={`Add a kind of work inside ${category.name}`}
          title={
            inside === null
              ? `Put something inside ${category.name} to make it a group.`
              : `Add a kind of work to ${category.name}.`
          }
          onClick={onAddInside}
        >
          <Icon name="plus" className="size-3.5" />
        </Button>
      ) : null}

      <Button
        size="sm"
        variant="ghost"
        data-no-drag
        aria-label={retired ? `Bring back ${category.name}` : `Retire ${category.name}`}
        title={
          retired
            ? 'Offer this again on new jobs.'
            : 'Take this off every picker. It stays on the jobs that already have it.'
        }
        onClick={onArchive}
      >
        {retired ? 'Bring back' : 'Retire'}
      </Button>

      {/* Delete is only for a row nothing points at. Offering it against 16,000
          jobs and letting the server refuse teaches people the button is
          broken; the tooltip says what to use instead. */}
      <Button
        size="sm"
        variant="ghost"
        data-no-drag
        disabled={held > 0}
        aria-label={`Delete ${category.name}`}
        title={
          held > 0 ? 'Something is using this, so it can only be retired.' : `Delete ${category.name}`
        }
        onClick={onDelete}
      >
        <Icon name="trash" className="size-3.5" />
      </Button>
    </div>
  )
}

/**
 * The row that is not saved yet.
 *
 * It stays open after a save, because adding one kind of work to a group is
 * rare and adding four in a row is what actually happens.
 */
function DraftRow({
  placeholder,
  indented = false,
  used,
  pending,
  onSave,
  onCancel,
}: {
  placeholder: string
  /** Inside a group, so it lines up with the rows it is joining. */
  indented?: boolean
  used: string[]
  pending: boolean
  onSave: (name: string, colour: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [colour, setColour] = useState(() => nextColour(used))

  const save = () => {
    const trimmed = name.trim()
    if (!trimmed) return onCancel()

    onSave(trimmed, colour)
    setName('')
    setColour(nextColour([...used, colour]))
  }

  return (
    <li
      className={cn(
        'flex items-center gap-2 bg-brand-50/40 py-[var(--row-pad-y)] pr-[var(--row-pad-x)]',
        indented ? 'pl-6' : 'pl-[var(--row-pad-x)]',
      )}
    >
      <span className="w-4 shrink-0" aria-hidden />
      <ColourPicker value={colour} label="Colour of the new kind of work" onChange={setColour} />

      <Input
        autoFocus
        value={name}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-32 flex-1"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save()
          if (event.key === 'Escape') onCancel()
        }}
      />

      <Button size="sm" variant="secondary" loading={pending} onClick={save}>
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Done
      </Button>
    </li>
  )
}

/**
 * The full form at the foot of the card.
 *
 * The inline draft rows cover "another one of these"; this covers the case
 * where somebody knows the name, the group and the colour up front and would
 * rather say all three at once than find the right row to press a plus on.
 */
function NewCategoryForm({
  groupChoices,
  used,
  pending,
  onSave,
}: {
  groupChoices: Category[]
  used: string[]
  pending: boolean
  onSave: (input: { name: string; colour: string; parentId: string | null }) => void
}) {
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState('')
  const [colour, setColour] = useState(() => nextColour(used))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return

    onSave({ name: name.trim(), colour, parentId: parentId || null })
    setName('')
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-end gap-2 border-t border-ink-200 p-[var(--card-pad)]"
    >
      <div className="min-w-40 flex-1">
        <Field label="New kind of work" htmlFor="category-name">
          <Input
            id="category-name"
            value={name}
            placeholder="Annual termite inspection"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
      </div>

      <div className="min-w-36">
        <Field label="Inside" htmlFor="category-parent">
          <select
            id="category-parent"
            aria-label="Inside"
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
            className="h-[var(--control-md)] min-w-28 rounded-md border border-ink-200 bg-white px-2 text-xs text-ink-800"
          >
            <option value="">No group</option>
            {groupChoices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <ColourPicker value={colour} label="Colour of the new kind of work" onChange={setColour} />

      <Button type="submit" variant="secondary" loading={pending}>
        Add
      </Button>
    </form>
  )
}

/**
 * The row under the pointer during a drag.
 *
 * Fixed to the viewport and positioned by the gesture rather than by React, so
 * a pointermove is a style write and not thirty rows of reconciliation. Nothing
 * in it is hit-testable: it sits under the cursor, and a band has to be able to
 * see the pointer arrive.
 */
function CarriedRow({
  ref,
  category,
  landing,
}: {
  ref: React.Ref<HTMLDivElement>
  category: Category
  landing: string | null
}) {
  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-50 flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs shadow-lg"
    >
      <span
        className="size-3 rounded-sm ring-1 ring-inset ring-black/10"
        style={{ backgroundColor: category.colour }}
      />
      <span className="font-medium text-ink-900">{category.name}</span>
      {landing ? <span className="text-ink-500">→ {landing}</span> : null}
    </div>
  )
}
